import React from 'react';
import theme from './ProductMoreInfoModal.module.scss';
import { ExternalLink } from '@ygtechnologies/bygr-frontend-component-library';
import { Accordion, Carousel } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { ProductDetails } from '@/seller/components/products/ProductCard/ProductDetails';
import { BaseProduct } from '@/types';
import SmallAttachmentList from '@/components/SmallAttachmentList';
import { ConfiguratorProductDto } from '@/services/configurator';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import { ProductImagePlaceholder } from '@/components/ProductImagePlaceholder';
import { useImageUrls } from '../useImageUrls';

export interface ProductMoreInfoModalContentProps {
    product: BaseProduct | ConfiguratorProductDto;
    headerContent?: React.ReactNode;
    showCategory?: boolean;
}

export const ProductMoreInfoModalContent: React.FC<ProductMoreInfoModalContentProps> = ({ product, headerContent, showCategory }) => {
    const { getProjectCatalogProductFileUrl, getProjectCatalogProductFileThumbnailUrl } = useImageUrls();
    const hasDetails = (product: BaseProduct | ConfiguratorProductDto) => {
        return product.widthCm !== null || product.heightCm !== null || product.lengthCm !== null || product.depthCm !== null || (product.sku !== null && product.sku !== "");
    };

    const hasInformation = (product: BaseProduct | ConfiguratorProductDto) => {
        return product.description !== null && product.description !== "" || product.productLink !== null && product.productLink !== "";
    };
    return (
        <div className={theme.content}>
            <div className={theme.headerAndImage}>
                <div>
                    <h2>{product.name}</h2>
                    {showCategory && product.itemCategoryId && (
                        <ItemCategoryDisplay className={theme.category} value={product.itemCategoryId} />
                    )}
                </div>
                {headerContent}
                {product.images.length > 1 ? (
                    <div className={theme.imageContainer}>
                        <Carousel
                            className={theme.carousel}
                            width={330}
                            height={200}
                            src={product.images.map((image) => ({
                                type: 'image',
                                src: getProjectCatalogProductFileUrl(image.id),
                                thumbnailSrc: getProjectCatalogProductFileThumbnailUrl(image.id),
                                title: product.name,
                            }))}
                        />
                    </div>
                ) : product.images.length === 1 ? (
                    <div className={theme.imageContainer}>
                        <img
                            className={theme.carousel}
                            src={getProjectCatalogProductFileUrl(product.images[0].id)}
                            alt={product.name}
                        />
                    </div>
                ) : (
                    <div className={theme.imageContainer}>
                        <ProductImagePlaceholder className={theme.imagePlaceholder} />
                    </div>
                )}
            </div>
            <div className={theme.infoSide}>
                <div>
                    <Accordion defaultOpen title={<Trans>Product information</Trans>}>
                        {hasInformation(product) ? (
                            <>
                                <p>{product.description}</p>
                                {product.productLink && (
                                    <ExternalLink className={theme.noCaption} href={product.productLink} target="_blank">
                                        <Trans>Product link</Trans>
                                    </ExternalLink>
                                )}
                            </>
                        ) : <Trans><span className="text-missing">No product information available</span></Trans>}
                    </Accordion>

                    {hasDetails(product) && (
                        <Accordion defaultOpen title={<Trans>Product details</Trans>}>
                            <ProductDetails layout="list" product={product} />
                        </Accordion>
                    )}

                    {
                        product.attachments.length > 0 && (
                            <Accordion defaultOpen title={<Trans>Attachments</Trans>}>
                                <SmallAttachmentList attachments={product.attachments} urlGenerator={(att) => getProjectCatalogProductFileUrl(att.id)} />
                            </Accordion>
                        )
                    }
                </div>
            </div>
        </div>
    );
};
